import { useEffect } from 'react';

import { ThemeProvider } from '@change/ds-core-ui/next';

import { I18nOverrides, Translate } from '@change-corgi/core/react/i18n';
import { DesignSystemLegacy } from '@change-corgi/design-system-legacy/core';
import { createModalHook, ModalsContainer } from '@change-corgi/design-system-legacy/modals';
import { sunriseTheme } from '@change-corgi/design-system-legacy/theme';

import I18N_PLACEHOLDERS from 'config/i18n/placeholders/petitionGamma/petition-gamma.yml';

import type { FormValues } from 'src/app/pages/petitionDetails/shared/types';

import { useIsSunriseEnabled } from '../../hooks/isSunriseEnabled';
import { SignFormContextProvider } from '../../hooks/signFormContext';
import { preloadSignForm, SignFormContainer } from '../signFormSunrise';

import { MobileModalContextDecorator } from './shared/MobileModalContextDecorator';
import type { MobileModalContext } from './shared/types';

const PRELOAD_SIGN_FORM_DELAY = 5000; // 5s

export const useMobileSignModal = createModalHook<MobileModalContext, undefined, FormValues>({
	name: 'MobileSignModal',
	variant: 'fullPage',
	closeOnBreakpoints: [false, true],
	title: function MobileSignModalTitle() {
		return (
			<ThemeProvider>
				<div className="mb-8 mt-1">
					<Translate value="fe.components.petition_sign.complete_signature" />
				</div>
			</ThemeProvider>
		);
	},
	body: function MobileSignModalBody({ persistentState, updatePersistentState }) {
		const {
			data: { isSunriseEnabled },
		} = useIsSunriseEnabled();
		return (
			/* temporary solution to allow the px modal on top of the sign modal */
			/* TODO refactor the workflow to avoid stacking modals on top of each other */
			<ModalsContainer>
				<div data-qa="sign-form-modal-container">
					<I18nOverrides translations={I18N_PLACEHOLDERS}>
						<SignFormContextProvider insideMobileModal>
							<ThemeProvider>
								<DesignSystemLegacy theme={isSunriseEnabled ? sunriseTheme : undefined}>
									<SignFormContainer previousState={persistentState} onChange={updatePersistentState} />
								</DesignSystemLegacy>
							</ThemeProvider>
						</SignFormContextProvider>
					</I18nOverrides>
				</div>
			</ModalsContainer>
		);
	},
	decorator: ({ children, options }) => (
		<MobileModalContextDecorator {...options}>{children}</MobileModalContextDecorator>
	),
	// just in case we attempt to open it twice (see comment in src/app/pages/petitionDetails/containers/mobileActionButton/MobileSignButton.tsx)
	killOpenModal: true,
});

export function usePreloadMobileSignModal(): void {
	// preload the sign form chunk after a few seconds
	useEffect(() => {
		const timeout = window.setTimeout(() => {
			void preloadSignForm();
		}, PRELOAD_SIGN_FORM_DELAY);
		return () => window.clearTimeout(timeout);
	}, []);
}
